import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, MapPin, Navigation, CheckCircle2, Trash2, Pencil, Droplet, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { PhotoUploader } from "@/components/PhotoUploader";
import { MultiPhotoUploader } from "@/components/MultiPhotoUploader";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";

const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

/** Per-pool row inside the review dialog: access info, delivery photos, gallons. */
function ReviewPoolRow({ d, idx, editing, setEditing, entryEdit, colonyId }) {
  const { entryEditIdx, entryEditDraft, setEntryEditDraft, startEntryEdit, saveEntryEdit, cancelEntryEdit } = entryEdit;
  return (
    <div className="grid grid-cols-12 gap-2 items-start py-2 border-b border-border/40 last:border-b-0" data-testid={`edit-pool-${idx}`}>
      <div className="col-span-5">
        <div className="font-medium text-sm">{d.pool_name}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {d.pool_address}</div>
        {/* Access info (entry photo + combo from pool master, snapshotted on the ticket) */}
        {entryEditIdx === idx ? (
          <div className="mt-2 border border-primary/40 bg-primary/5 rounded-sm p-3 space-y-2" data-testid={`edit-entry-form-${idx}`}>
            <div className="overline text-primary text-[10px]">Edit access info</div>
            <PhotoUploader
              value={entryEditDraft.entry_photo_data_url}
              onChange={(v) => setEntryEditDraft({ ...entryEditDraft, entry_photo_data_url: v })}
              label="Entry / gate photo"
              testId={`edit-entry-photo-upload-${idx}`}
              colonyId={colonyId}
            />
            <Textarea
              className="rounded-sm font-mono text-xs"
              placeholder="e.g. Gate code 1234#&#10;Lockbox by garage, combo 4-9-1"
              rows={3}
              value={entryEditDraft.entry_instructions}
              onChange={(e) => setEntryEditDraft({ ...entryEditDraft, entry_instructions: e.target.value })}
              data-testid={`edit-entry-text-input-${idx}`}
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={cancelEntryEdit} data-testid={`edit-entry-cancel-${idx}`}>Cancel</Button>
              <Button size="sm" className="rounded-sm h-8" onClick={() => saveEntryEdit(idx)} data-testid={`edit-entry-save-${idx}`}>Save</Button>
            </div>
          </div>
        ) : (
          <div className="mt-2 border border-primary/30 bg-primary/5 rounded-sm p-2" data-testid={`edit-entry-${idx}`}>
            <div className="flex items-start gap-2">
              {d.entry_photo_data_url && (
                <LivePhotoSurface value={d.entry_photo_data_url} alt="entry" className="w-14 h-14 rounded-sm border border-primary/30 flex-shrink-0" testId={`edit-entry-photo-${idx}`} />
              )}
              <div className="text-[11px] leading-snug min-w-0 flex-1">
                <div className="font-semibold text-primary uppercase tracking-wider text-[9px] mb-0.5 flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Access info
                </div>
                <div className="font-mono whitespace-pre-wrap break-words" data-testid={`edit-entry-text-${idx}`}>
                  {d.entry_instructions || (d.entry_photo_data_url ? "(Photo only)" : "No access info on file.")}
                </div>
              </div>
              {editing.status !== "closed" && (
                <Button size="sm" variant="ghost" className="rounded-sm h-7 text-primary hover:text-primary flex-shrink-0" onClick={() => startEntryEdit(idx, d)} data-testid={`edit-entry-toggle-${idx}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              )}
            </div>
          </div>
        )}
        {/* Driver's BEFORE / AFTER delivery photos */}
        {(d.photo_before_data_url || d.photo_after_data_url) && (
          <div className="mt-2 flex gap-2" data-testid={`edit-delivery-photos-${idx}`}>
            {d.photo_before_data_url ? (
              <div className="block" data-testid={`edit-photo-before-${idx}`}>
                <LivePhotoSurface value={d.photo_before_data_url} alt="before" className="w-20 h-20 rounded-sm border border-border" testId={`edit-photo-before-media-${idx}`} />
                <div className="text-[10px] text-muted-foreground mt-0.5 font-mono tracking-wider uppercase">Before</div>
              </div>
            ) : (
              <div className="w-20 h-[88px] flex items-center justify-center text-[10px] text-muted-foreground border border-dashed border-border rounded-sm">No before</div>
            )}
            {d.photo_after_data_url ? (
              <div className="block" data-testid={`edit-photo-after-${idx}`}>
                <LivePhotoSurface value={d.photo_after_data_url} alt="after" className="w-20 h-20 rounded-sm border border-border" testId={`edit-photo-after-media-${idx}`} />
                <div className="text-[10px] text-muted-foreground mt-0.5 font-mono tracking-wider uppercase">After</div>
              </div>
            ) : (
              <div className="w-20 h-[88px] flex items-center justify-center text-[10px] text-muted-foreground border border-dashed border-border rounded-sm">No after</div>
            )}
          </div>
        )}
      </div>
      <div className="col-span-3 text-xs text-muted-foreground pt-1">Tank: <span className="mono-num">{d.tank_size}</span> gal</div>
      <div className="col-span-3 flex items-center gap-1">
        <Input type="number" step="0.1" min="0" disabled={editing.status === "closed"} className="rounded-sm mono-num text-right font-semibold" value={d.gallons}
               onChange={(e) => {
                 const v = parseFloat(e.target.value || 0);
                 const next = editing.pool_deliveries.map((x, i) => i === idx ? { ...x, gallons: v } : x);
                 setEditing({ ...editing, pool_deliveries: next });
               }} data-testid={`edit-pool-gallons-${idx}`} />
        <span className="text-xs text-muted-foreground">gal</span>
      </div>
      <a href={mapsUrl(d.pool_address)} target="_blank" rel="noopener noreferrer" className="col-span-1 text-primary text-xs hover:underline flex items-center justify-center pt-1"><Navigation className="w-4 h-4" /></a>
    </div>
  );
}

/**
 * Admin "Review & close ticket" dialog. Parent owns `editing` (the ticket being
 * reviewed, with _items enriched); this component owns confirmation + entry-edit state.
 */
export default function TicketReviewDialog({ editing, setEditing, products, settings, onChanged, onDelete }) {
  const [gallonsConfirmed, setGallonsConfirmed] = useState(false);
  const [entryEditIdx, setEntryEditIdx] = useState(null);
  const [entryEditDraft, setEntryEditDraft] = useState({ entry_photo_data_url: "", entry_instructions: "" });
  const [productPick, setProductPick] = useState("");
  const [qtyPick, setQtyPick] = useState(1);

  const closeDialog = () => { setEditing(null); setGallonsConfirmed(false); setEntryEditIdx(null); };

  const startEntryEdit = (idx, d) => {
    setEntryEditIdx(idx);
    setEntryEditDraft({
      entry_photo_data_url: d.entry_photo_data_url || "",
      entry_instructions: d.entry_instructions || "",
    });
  };

  const saveEntryEdit = async (idx) => {
    if (!editing) return;
    const d = editing.pool_deliveries[idx];
    if (!d?.pool_id) return toast.error("Missing pool reference");
    try {
      await api.patch(`/pools/${d.pool_id}/entry`, {
        entry_photo_data_url: entryEditDraft.entry_photo_data_url || "",
        entry_instructions: entryEditDraft.entry_instructions || "",
      });
      const next = editing.pool_deliveries.map((x, i) => i === idx ? {
        ...x,
        entry_photo_data_url: entryEditDraft.entry_photo_data_url || "",
        entry_instructions: entryEditDraft.entry_instructions || "",
      } : x);
      setEditing({ ...editing, pool_deliveries: next });
      setEntryEditIdx(null);
      toast.success("Entry info updated");
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  // Build the JSON payload sent to PUT /tickets/{id}. Carboy fields are always
  // included so the driver's updated qty (or "I didn't refill any") survives the
  // close call and ends up on the resulting invoice.
  const ticketPayload = () => ({
    delivery_date: editing.delivery_date,
    notes: editing.notes || "",
    pool_deliveries: (editing.pool_deliveries || []).map(({ pool_id, gallons }) => ({ pool_id, gallons: Number(gallons || 0) })),
    gallons: Number(editing.gallons || 0),
    items: (editing._items || []).map(({ product_id, quantity }) => ({ product_id, quantity })),
    refill_carboys: !!editing.refill_empty_jugs,
    refill_carboys_qty: editing.refill_empty_jugs ? Number(editing.refill_carboys_qty || 0) : 0,
    // Optional proof-of-delivery photos for products-only tickets (admin can
    // attach signed receipts, "left at gate" snaps, etc. — same field the
    // driver writes from Driver Day).
    delivery_photo_data_urls: editing.delivery_photo_data_urls || [],
  });

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/tickets/${editing.id}`, ticketPayload());
      toast.success("Ticket updated");
      closeDialog();
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const closeTicket = async () => {
    const t = editing;
    if (!t) return;
    if (!gallonsConfirmed && !isProductsOnly) {
      toast.error("Confirm the gallons delivered before closing the ticket.");
      return;
    }
    const hasGallons = (t.pool_deliveries || []).some((d) => Number(d.gallons || 0) > 0) || Number(t.gallons || 0) > 0;
    const hasItems = (t._items || []).length > 0;
    if (!hasGallons && !hasItems) {
      toast.error("Enter gallons or at least one product before closing.");
      return;
    }
    const missingPhotos = (t.pool_deliveries || []).filter(
      (d) => !((d.photo_before_data_url || "").trim() && (d.photo_after_data_url || "").trim())
    );
    if (missingPhotos.length > 0) {
      toast.error(`Before AND after photos required for: ${missingPhotos.map((d) => d.pool_name || "(unnamed)").join(", ")}`);
      return;
    }
    // Products-only tickets have nothing to double-check — close without a confirm popup.
    if (!isProductsOnly && !window.confirm("Close this ticket and generate the invoice? Make sure the gallons are correct first.")) return;
    try {
      await api.put(`/tickets/${t.id}`, ticketPayload());
      const r = await api.post(`/tickets/${t.id}/close`);
      toast.success(`Ticket closed → Invoice ${r.data.number} created & email sent`);
      closeDialog();
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const previewSubtotal = (t) => {
    const price = Number(settings.price_per_gallon || 5);
    const poolTotal = (t.pool_deliveries || []).reduce((s, d) => s + (Number(d.gallons || 0) * price), 0);
    const fallback = (t.pool_deliveries?.length || 0) === 0 ? Number(t.gallons || 0) * price : 0;
    const itemTotal = (t._items || t.items || []).reduce((s, it) => {
      const p = products.find((x) => x.id === it.product_id);
      return s + ((p?.price || it._price || 0) * Number(it.quantity || 0));
    }, 0);
    return poolTotal + fallback + itemTotal;
  };

  const addProduct = () => {
    if (!productPick) return toast.error("Pick a product");
    const p = products.find((x) => x.id === productPick);
    if (!p) return;
    if (!qtyPick || qtyPick <= 0) return toast.error("Quantity must be > 0");
    setEditing({ ...editing, _items: [...(editing._items || []), { product_id: p.id, quantity: Number(qtyPick), _name: p.name, _price: p.price }] });
    setProductPick(""); setQtyPick(1);
  };

  const entryEdit = { entryEditIdx, entryEditDraft, setEntryEditDraft, startEntryEdit, saveEntryEdit, cancelEntryEdit: () => setEntryEditIdx(null) };
  const photosMissing = (editing?.pool_deliveries || []).some((d) => !((d.photo_before_data_url || "").trim() && (d.photo_after_data_url || "").trim()));
  const isProductsOnly = !!editing && (editing.products_only ||
    ((editing.pool_deliveries || []).length === 0 && Number(editing.gallons || 0) <= 0 && (editing._items || []).length > 0));

  return (
    <Dialog open={!!editing} onOpenChange={(v) => { if (!v) closeDialog(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{editing?.status === "closed" ? "Ticket details" : "Review & close ticket"}</DialogTitle>
          <DialogDescription>{editing?.status === "closed" ? "Read-only view of this closed ticket — access info, gallons per pool, and the driver's delivery photos." : "Confirm the gallons delivered to each pool, then close the ticket to generate the invoice."}</DialogDescription>
        </DialogHeader>
        {editing && (
          <div className="space-y-4">
            {editing.refill_empty_jugs && !isProductsOnly && (
              <div className="border border-primary bg-primary/5 rounded-sm px-4 py-3 flex items-start gap-3" data-testid="edit-jugs-banner">
                <Droplet className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-sm text-primary">Refill empty 5-gallon jugs on-site</div>
                  <div className="text-xs text-muted-foreground">Update the number of carboys you actually swapped — this flows straight to the invoice.</div>
                </div>
                {editing.status !== "closed" && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Label className="text-xs font-semibold">Qty</Label>
                    <Input type="number" min="0" step="1" className="rounded-sm w-20 mono-num text-center"
                           value={editing.refill_carboys_qty ?? 0}
                           onChange={(e) => setEditing({ ...editing, refill_carboys_qty: Math.max(0, parseInt(e.target.value || 0)) })}
                           data-testid="review-carboys-qty" />
                  </div>
                )}
                {editing.status === "closed" && (
                  <div className="text-sm font-bold mono-num flex-shrink-0">{editing.refill_carboys_qty || 0} ×</div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Customer</Label>
                <Input className="rounded-sm" value={editing.business_name || editing.customer_name} disabled />
              </div>
              <div>
                <Label>Delivery date</Label>
                <Input type="date" className="rounded-sm" value={editing.delivery_date}
                       onChange={(e) => setEditing({ ...editing, delivery_date: e.target.value })} />
              </div>
            </div>

            {isProductsOnly ? (
              <div className="border border-primary/40 bg-primary/5 p-4" data-testid="review-products-only-banner">
                <p className="font-bold text-sm text-primary">Products-only ticket — no chlorine</p>
                <p className="text-xs text-muted-foreground">No gallons to confirm and no tank photos required. Closing invoices the products below.</p>
              </div>
            ) : (
            <div className="border border-border p-4 space-y-2">
              <p className="overline">Gallons per pool · {(editing.pool_deliveries || []).length} pool{(editing.pool_deliveries || []).length === 1 ? "" : "s"}</p>
              {(editing.pool_deliveries || []).length === 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Gallons delivered</Label>
                  <Input type="number" step="0.1" className="rounded-sm w-32 mono-num" value={editing.gallons || 0}
                         onChange={(e) => setEditing({ ...editing, gallons: parseFloat(e.target.value || 0) })} data-testid="edit-fallback-gallons" />
                </div>
              )}
              {(editing.pool_deliveries || []).map((d, idx) => (
                <ReviewPoolRow key={d.pool_id} d={d} idx={idx} editing={editing} setEditing={setEditing} entryEdit={entryEdit} colonyId={editing.colony_id} />
              ))}
            </div>
            )}

            {/* Optional delivery photos — only on products-only tickets to
                mirror what drivers see on Driver Day (chlorine tickets already
                require per-pool before/after photos). Same field; admin/
                secretary can attach signed receipts, "left at gate" snaps,
                etc. Not AI-validated; purely a paper trail on the invoice. */}
            {isProductsOnly && (
              <div className="border border-border p-4 space-y-2" data-testid="edit-delivery-photos-section">
                <p className="overline">Delivery photos (optional) · {(editing.delivery_photo_data_urls || []).length}</p>
                {editing.status === "closed" ? (
                  (editing.delivery_photo_data_urls || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">No delivery photos attached to this ticket.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {editing.delivery_photo_data_urls.map((url, i) => (
                        <div key={i} className="block" data-testid={`edit-delivery-photo-${i}`}>
                          <LivePhotoSurface value={url} alt={`delivery ${i + 1}`} className="w-20 h-20 rounded-sm border border-border" testId={`edit-extra-delivery-photo-${i}`} />
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  <MultiPhotoUploader
                    value={editing.delivery_photo_data_urls || []}
                    onChange={(urls) => setEditing({ ...editing, delivery_photo_data_urls: urls })}
                    label="Receipts / drop-off photos"
                    testId="edit-delivery-photos"
                    colonyId={editing.colony_id}
                  />
                )}
              </div>
            )}

            {/* Additional products (review-time) */}
            <div className="border border-border p-4 space-y-3">
              <p className="overline">Additional products · {(editing._items || []).length}</p>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-7">
                  <Label>Add product</Label>
                  <Select value={productPick} onValueChange={setProductPick}>
                    <SelectTrigger className="rounded-sm" data-testid="edit-product-select"><SelectValue placeholder={products.length ? "Select product" : "No active products"} /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {fmtMoney(p.price)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3"><Label>Qty</Label><Input type="number" min="1" step="1" className="rounded-sm mono-num" value={qtyPick} onChange={(e) => setQtyPick(parseInt(e.target.value || 1))} data-testid="edit-product-qty" /></div>
                <div className="col-span-2">
                  <Button type="button" variant="outline" className="rounded-sm w-full" data-testid="edit-add-product" onClick={addProduct}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {(editing._items || []).length > 0 && (
                <div className="border-t border-border pt-2 space-y-1">
                  {editing._items.map((it, i) => (
                    <div key={`${it.product_id}-${i}`} className="flex items-center justify-between text-sm py-1.5" data-testid={`edit-item-${i}`}>
                      <span className="flex-1">{it._name}</span>
                      <span className="mono-num w-16 text-right">{it.quantity}×</span>
                      <span className="mono-num w-24 text-right">{fmtMoney(it._price)}</span>
                      <span className="mono-num w-24 text-right font-semibold">{fmtMoney(it._price * it.quantity)}</span>
                      <Button size="sm" variant="ghost" className="rounded-sm ml-2 h-7 w-7 p-0"
                        onClick={() => setEditing({ ...editing, _items: editing._items.filter((_, idx) => idx !== i) })}
                        data-testid={`edit-item-remove-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div><Label>Notes</Label><Textarea className="rounded-sm" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>

            <div className="flex justify-between items-center text-sm pt-2 border-t border-border">
              <span className="overline">Subtotal preview</span>
              <span className="font-display text-2xl font-black tracking-tighter mono-num">{fmtMoney(previewSubtotal(editing))}</span>
            </div>

            {editing.status !== "closed" && !isProductsOnly && (
            <label className="flex items-start gap-3 border border-primary/40 bg-primary/5 rounded-sm px-4 py-3 cursor-pointer" data-testid="gallons-confirm-block">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={gallonsConfirmed}
                     onChange={(e) => setGallonsConfirmed(e.target.checked)} data-testid="gallons-confirm-checkbox" />
              <div>
                <div className="font-bold text-sm text-primary">I confirm the gallons delivered are correct</div>
                <div className="text-xs text-muted-foreground">Required before generating the invoice. Double-check each pool before checking this box.</div>
              </div>
            </label>
            )}
          </div>
        )}
        {editing?.status === "closed" ? (
          <DialogFooter className="flex-wrap gap-2">
            {editing.invoice_id && (
              <Link to={`/admin/invoices/${editing.invoice_id}`} className="rounded-sm h-9 px-4 inline-flex items-center text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="edit-view-invoice-link">
                View invoice
              </Link>
            )}
            <Button onClick={closeDialog} variant="outline" className="rounded-sm">Close</Button>
          </DialogFooter>
        ) : (
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onDelete(editing)} className="rounded-sm text-destructive border-destructive hover:bg-destructive/10" data-testid="edit-delete-button">
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={closeDialog} className="rounded-sm">Cancel</Button>
          <Button onClick={saveEdit} variant="outline" className="rounded-sm" data-testid="edit-save-button">
            Save without closing
          </Button>
          <Button onClick={closeTicket} disabled={(!gallonsConfirmed && !isProductsOnly) || photosMissing} className="rounded-sm" data-testid="edit-close-button">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Close ticket → Invoice
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
